import React from "react";
import { useParams } from "react-router-dom";
import ShopNav from '../../../components/customer/ShopNav';
import { MeetingType } from "../../../services/meetingTypeService";
import CustomerHeaderControls from "../../../components/customer/CustomerHeaderControls";
import { isAccessLocked } from "../../../utils/meetingTypeAccess";

interface BookingPageHeaderProps {
  meetingType: MeetingType | null;
  loading: boolean;
}

/**
 * Header strip for the booking page.
 *
 * Carries the CUSTOMER's controls: where to buy what this page asks for, and
 * the sign-in button / points chip — both only when the meeting type is locked
 * to points or a plan.
 *
 * The host's copy-link button used to live here too. It was removed: the same
 * link is already offered from the meeting-type list and the editor, and a
 * host-only control sitting in an invitee-facing bar made the bar's purpose
 * ambiguous.
 */
const BookingPageHeader: React.FC<BookingPageHeaderProps> = ({
  meetingType,
  loading,
}) => {
  // The first URL segment — the same handle the shop pages are addressed by.
  const { username } = useParams<{ username: string }>();

  // The meeting type requires points and/or a plan, so an invitee needs the
  // sign-in control. That is now the ONLY reason this bar exists — with the
  // host's copy-link button gone, a host on an unlocked page would otherwise
  // get an empty strip.
  /**
   * ⚠️ THE EFFECTIVE requirement, not the raw column.
   *
   * A page priced in points at the meeting-type level leaves
   * `accessRequirement` at 'none', so this whole strip returned null and a
   * points page had NO sign-in control at all — a returning customer with a
   * balance had nowhere to say who they were. See `meetingTypeAccess`.
   */
  const isLocked = isAccessLocked(meetingType);

  if (!isLocked) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-white border-b border-secondary-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex justify-end">
            <div className="h-9 bg-secondary-200 rounded w-24 animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!meetingType) {
    return null;
  }

  return (
    <div className="bg-white border-b border-secondary-200 shadow-sm">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-end gap-3">
          {/* Where to buy what this page asks for. Only on a locked page: an
              open booking page needs nothing bought, so a shop link there would
              be an advert rather than navigation. */}
          {username && <ShopNav username={username} />}

          <CustomerHeaderControls
            meetingTypeId={meetingType.id}
            organizationId={(meetingType as any).organizationId}
            username={username}
            locked={isLocked}
          />

        </div>
      </div>
    </div>
  );
};

export default BookingPageHeader;
